import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Button,
} from '@heroui/react';
import { Icon } from '@iconify/react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useSegment from '@/hooks/useSegment';

import chatbotStyles from './styles/chatbot.module.css';

interface ChatbotFeedbackProps {
  responseId: string;
  submitFeedback: (id: string, helpful: boolean, comment?: string) => void;
  feedback?: {
    helpful: boolean | null;
    comment: string | null;
  };
}

const EVENT_THUMBS_DOWN = 'Education Feedback Thumbs Down';

export function ChatbotFeedback({
  responseId,
  submitFeedback,
  feedback,
}: ChatbotFeedbackProps) {
  const { t } = useTranslation('education');
  const { sendTrackEvent } = useSegment();

  const [internalSelectedBtn, setInternalSelectedBtn] = useState('');
  const [modal, setModal] = useState(false);
  const [draftComment, setDraftComment] = useState('');

  // Use draft comment when modal is open (for editing), otherwise use feedback comment if available
  const comment = modal ? draftComment : (feedback?.comment ?? '');

  const handleCommentChange = (value: string) => {
    // Only update draft comment while typing, don't persist yet
    setDraftComment(value);
  };

  // Use feedback helpful value if provided, otherwise use internal state
  // Convert boolean to 'yes'/'no' string for styling purposes
  const selectedBtn =
    feedback?.helpful !== null && feedback?.helpful !== undefined
      ? feedback.helpful
        ? 'yes'
        : 'no'
      : internalSelectedBtn;

  const handlePositiveFeedback = () => {
    const newState = 'yes';
    setInternalSelectedBtn(newState);
    submitFeedback(responseId, true);
  };

  const handleNegativeFeedbackClick = () => {
    setInternalSelectedBtn('no');
    // Initialize draft comment from feedback comment if available, but only if draft is empty
    if (feedback?.comment && !draftComment) {
      setDraftComment(feedback.comment);
    }
    setModal(true);
    // Submit feedback immediately when "Not helpful" is clicked
    submitFeedback(responseId, false);
  };

  const handleModalClose = () => {
    setModal(false);
  };

  const handleSubmit = () => {
    setModal(false);
    // Submit comment to backend API if provided
    if (draftComment.trim()) {
      submitFeedback(responseId, false, draftComment.trim());
      // Also track comment via Segment
      sendTrackEvent(EVENT_THUMBS_DOWN, {
        id: responseId,
        comment: draftComment.trim(),
      });
      // Clear draft after submitting
      setDraftComment('');
    }
  };

  return (
    <>
      <Modal isOpen={modal} onClose={handleModalClose}>
        <ModalContent>
          <ModalHeader>{t('chatbotfeedback.form.title')}</ModalHeader>
          <ModalBody>
            <Textarea
              onChange={(e) => handleCommentChange(e.target.value)}
              placeholder={t('common.input.text_placeholder')}
              value={comment}
            />
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={handleSubmit}>
              {t('common.action.submit')}
            </Button>
            <Button
              color="primary"
              variant="bordered"
              onPress={handleModalClose}
            >
              {t('common.action.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <div className={chatbotStyles.feedbackContainer}>
        <span className={chatbotStyles.feedbackLabel}>
          {t('chatbotfeedback.title')}
        </span>
        <Button
          className={`${chatbotStyles.feedbackBtn} ${selectedBtn === 'yes' ? chatbotStyles.feedbackBtnActiveUp : ''}`}
          onPress={handlePositiveFeedback}
          isIconOnly
          variant="light"
          size="sm"
          radius="full"
          aria-label={t('chatbotfeedback.button.helpful')}
          aria-pressed={selectedBtn === 'yes'}
        >
          <Icon icon="mdi:thumb-up-outline" className="w-4 h-4" />
        </Button>
        <Button
          className={`${chatbotStyles.feedbackBtn} ${selectedBtn === 'no' ? chatbotStyles.feedbackBtnActiveDown : ''}`}
          onPress={handleNegativeFeedbackClick}
          isIconOnly
          variant="light"
          size="sm"
          radius="full"
          aria-label={t('chatbotfeedback.button.not_helpful')}
          aria-pressed={selectedBtn === 'no'}
        >
          <Icon icon="mdi:thumb-down-outline" className="w-4 h-4" />
        </Button>
      </div>
    </>
  );
}
